import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { Menu, ArrowLeft, Home, History, Settings } from "lucide-react";
import { NotificationBell } from "./notifications/NotificationBell";

interface User {
  name: string;
  email: string;
  image?: string;
}

interface MobileHeaderProps {
  isAuthenticated?: boolean;
  user?: User;
  onSignInClick?: () => void;
  onSignOut?: () => void;
  showBackButton?: boolean;
  onBackClick?: () => void;
  title?: string;
}

export function MobileHeader({
  isAuthenticated = false,
  user,
  onSignInClick,
  onSignOut,
  showBackButton = false,
  onBackClick,
  title,
}: MobileHeaderProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();

  const handleNavigation = (path: string) => {
    navigate(path);
    setIsMenuOpen(false);
  };

  return (
    <header className="flex items-center justify-between p-4 bg-background border-b border-border">
      <div className="flex items-center gap-3">
        {showBackButton && onBackClick ? (
          <Button variant="ghost" size="sm" onClick={onBackClick}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
        ) : isAuthenticated ? (
          <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="sm">
                <Menu className="h-4 w-4" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-64">
              <div className="flex flex-col space-y-4 mt-8">
                <div className="px-2 py-1 border-b border-border pb-4">
                  <h2 className="font-sans font-semibold text-lg">Navigation</h2>
                </div>
                
                <Button
                  variant="ghost"
                  className="justify-start"
                  onClick={() => handleNavigation('/lobby')}
                >
                  <Home className="h-4 w-4 mr-2" />
                  Home
                </Button>
                
                <Button
                  variant="ghost"
                  className="justify-start"
                  onClick={() => handleNavigation('/past-games')}
                >
                  <History className="h-4 w-4 mr-2" />
                  Past Games
                </Button>

                {user?.email === 'caleb.brown@x-team.com' && (
                  <Button
                    variant="ghost"
                    className="justify-start"
                    onClick={() => handleNavigation('/admin')}
                  >
                    <Settings className="h-4 w-4 mr-2" />
                    Admin Panel
                  </Button>
                )}

                <div className="pt-4 border-t border-border">
                  <div className="px-2 py-2 text-sm text-muted-foreground">
                    Signed in as: <span className="font-mono">{user?.name}</span>
                  </div>
                  <Button
                    variant="outline"
                    className="w-full mt-2"
                    onClick={() => {
                      onSignOut?.();
                      setIsMenuOpen(false);
                    }}
                  >
                    Sign Out
                  </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        ) : null}
        
        <div className="flex items-center gap-2">
          <h1 className="font-sans font-semibold text-lg">
            {title || "X-Team Trivia"}
          </h1>
        </div>
      </div>

      {!isAuthenticated && (
        <Button variant="outline" size="sm" onClick={onSignInClick}>
          Sign In
        </Button>
      )}

      {isAuthenticated && user && !showBackButton && (
        <div className="flex items-center gap-2">
          <NotificationBell />
          <span className="text-sm font-mono hidden sm:inline">{user.name}</span>
        </div>
      )}
    </header>
  );
}
